import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { cn } from "@/components/ui/utils";
import { useContextTree } from "@/hooks/useContextTree";

/** Selection + hover styles aligned with ``SelectableCardList`` row chrome.

 * Hover paints the muted selection border. Keyboard focus on a card
 * overtakes that with the stronger focus border + outline (same layering
 * as list rows: pointer highlight, then keyboard selection wins).
 */
export function navigableCardSelectionClassName({
  isActive,
  isFocusWithin,
  suppressHover = false,
}: {
  isActive: boolean;
  isFocusWithin: boolean;
  suppressHover?: boolean;
}) {
  const keyboardOwnsBorder = isActive && isFocusWithin;
  return cn(
    isActive && "bg-muted",
    isActive &&
      (keyboardOwnsBorder
        ? "[border:1px_solid_var(--muted-foreground)]"
        : "[border:1px_solid_color-mix(in_srgb,var(--foreground)_25%,transparent_75%)]"),
    keyboardOwnsBorder
      ? "[outline:2px_solid_color-mix(in_srgb,var(--muted-foreground)_20%,transparent_80%)]"
      : "[outline:2px_solid_transparent]",
    "[outline-offset:0px]",
    !suppressHover && "hover:bg-muted",
    // Keyboard-focused card keeps its focus chrome; everything else gets the
    // muted hover border (including while focus lives elsewhere in the grid).
    !suppressHover &&
      !keyboardOwnsBorder &&
      "hover:border-[color-mix(in_srgb,var(--foreground)_25%,transparent_75%)]",
  );
}

function measureColumnCount(
  containerWidth: number,
  minColumnWidth: number,
  gapPx: number,
) {
  if (containerWidth <= 0) return 1;
  return Math.max(
    1,
    Math.floor((containerWidth + gapPx) / (minColumnWidth + gapPx)),
  );
}

/** Props to spread onto the root ``Card`` (or other focusable surface). */
export type NavigableCardRootProps = {
  tabIndex: number;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent) => void;
  ref: (el: HTMLElement | null) => void;
  className?: string;
  "data-row-index": number;
  "data-col-index": number;
};

export type NavigableCardGridRenderArgs = {
  index: number;
  rowIndex: number;
  colIndex: number;
  rootProps: NavigableCardRootProps;
  /** Keyboard/mouse focus is on this card. */
  isActive: boolean;
  /** Focus is inside the grid (arrow-key navigation). */
  isFocusWithin: boolean;
};

export type NavigableCardGridProps = {
  itemCount: number;
  minColumnWidth: number;
  onActivate: (index: number) => void;
  isItemDisabled?: (index: number) => boolean;
  renderItem: (args: NavigableCardGridRenderArgs) => ReactNode;
  getItemKey: (index: number) => string;
};

export function NavigableCardGrid({
  itemCount,
  minColumnWidth,
  onActivate,
  isItemDisabled,
  renderItem,
  getItemKey,
}: NavigableCardGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(1);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [isFocusWithin, setIsFocusWithin] = useState(false);

  const rowCount = useMemo(
    () => Math.max(1, Math.ceil(itemCount / columnCount)),
    [itemCount, columnCount],
  );

  const {
    Scope,
    ref: scopeRef,
    getCellProps,
    getCell,
  } = useContextTree({
    cellNavigation:
      itemCount > 0
        ? {
            rowCount,
            colCount: (rowIndex) =>
              Math.min(columnCount, itemCount - rowIndex * columnCount),
            wrap: false,
          }
        : undefined,
  });

  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;

    const update = () => {
      const gapPx = parseFloat(getComputedStyle(el).columnGap) || 16;
      setColumnCount(measureColumnCount(el.clientWidth, minColumnWidth, gapPx));
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [minColumnWidth]);

  useEffect(() => {
    if (itemCount === 0) return;
    requestAnimationFrame(() => {
      getCell(0, 0)?.focus();
      setFocusedIndex(0);
    });
  }, [itemCount, columnCount, getCell]);

  useEffect(() => {
    const scopeEl = scopeRef.current;
    if (!scopeEl) return;

    const syncFocusedIndex = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement) || !scopeEl.contains(target)) return;

      let el: HTMLElement | null = target;
      while (el && el !== scopeEl) {
        const rowRaw = el.dataset.rowIndex;
        const colRaw = el.dataset.colIndex;
        if (rowRaw !== undefined && colRaw !== undefined) {
          const rowIndex = parseInt(rowRaw, 10);
          const colIndex = parseInt(colRaw, 10);
          if (!Number.isNaN(rowIndex) && !Number.isNaN(colIndex)) {
            const index = rowIndex * columnCount + colIndex;
            if (index >= 0 && index < itemCount) {
              setFocusedIndex(index);
            }
          }
          return;
        }
        el = el.parentElement;
      }
    };

    const updateFocusState = (
      target: EventTarget | null = document.activeElement,
    ) => {
      syncFocusedIndex(target);
      const active = document.activeElement;
      setIsFocusWithin(!!active && scopeEl.contains(active));
    };

    const onFocusIn = (event: FocusEvent) => {
      updateFocusState(event.target);
    };

    const onFocusOut = () => {
      requestAnimationFrame(() => updateFocusState());
    };

    scopeEl.addEventListener("focusin", onFocusIn);
    scopeEl.addEventListener("focusout", onFocusOut);
    updateFocusState();

    return () => {
      scopeEl.removeEventListener("focusin", onFocusIn);
      scopeEl.removeEventListener("focusout", onFocusOut);
    };
  }, [columnCount, itemCount, scopeRef]);

  const scopeStyle = useMemo(
    () => ({
      display: "block",
      outline: "none",
    }),
    [],
  );

  return (
    <Scope ref={scopeRef} tabIndex={-1} style={scopeStyle}>
      <div
        ref={gridRef}
        className="grid grid-cols-[repeat(auto-fill,minmax(var(--min-column-width),1fr))] gap-4 p-4"
        style={{ "--min-column-width": `${minColumnWidth}px` } as CSSProperties}
      >
        {Array.from({ length: itemCount }, (_, index) => {
          const rowIndex = Math.floor(index / columnCount);
          const colIndex = index % columnCount;
          const cellProps = getCellProps({ rowIndex, colIndex });
          const disabled = isItemDisabled?.(index) ?? false;
          const isActive = focusedIndex === index;

          const activate = () => {
            if (disabled) return;
            onActivate(index);
          };

          const rootProps: NavigableCardRootProps = {
            ref: cellProps.ref,
            className: cellProps.className,
            "data-row-index": cellProps["data-row-index"],
            "data-col-index": cellProps["data-col-index"],
            tabIndex: disabled ? -1 : 0,
            onClick: activate,
            onKeyDown: (event) => {
              if (disabled) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                activate();
              }
            },
          };

          return (
            <div
              key={getItemKey(index)}
              className="flex min-w-0 h-full *:flex-1"
            >
              {renderItem({
                index,
                rowIndex,
                colIndex,
                rootProps,
                isActive,
                isFocusWithin,
              })}
            </div>
          );
        })}
      </div>
    </Scope>
  );
}
